import React from 'react';
import { Alert, Content } from '@patternfly/react-core';
import { formatDateTime } from '../../../utils/dateFormat';

interface InTransferAlertProps {
  /** The transfer deadline (ISO string) or null — rendered as "Complete by <date>" when present. */
  deadline: string | null;
  /** Which surface this is shown on — tunes the wording. */
  variant?: 'domain' | 'mail';
  /** Extra context-specific copy (e.g. the mailbox modal's "mailboxes are free until…" line). */
  children?: React.ReactNode;
  className?: string;
}

/**
 * The single source of the "prep is torn down if the transfer fails" warning, reused on every
 * surface where a customer can prepare a provisional (mid-transfer) domain: the domain detail page,
 * the domains list (compact), and the mail detail/create screens. A pending transfer creates a
 * provisional `domain_name` — any DNS/mail prepared on it is removed if the transfer doesn't
 * complete (unless the domain was already verified with us; the UI warns conservatively regardless).
 */
const InTransferAlert: React.FunctionComponent<InTransferAlertProps> = ({
  deadline,
  variant = 'domain',
  children,
  className,
}) => (
  <Alert variant="warning" isInline title="Transfer in progress" className={className}>
    <Content component="p">
      {variant === 'mail'
        ? "This domain is being transferred in. Any mail configuration you set up on it now is provisional and will be removed if the transfer doesn't complete (unless the domain was already verified with us)."
        : "This domain is being transferred in. Any DNS zone, nameservers, or mail you prepare on it now is provisional and will be removed if the transfer doesn't complete (unless the domain was already verified with us)."}
    </Content>
    {deadline !== null && (
      <Content component="p" className="pf-v6-u-mt-sm">
        Complete by <strong>{formatDateTime(deadline)}</strong>.
      </Content>
    )}
    {children}
  </Alert>
);

export default InTransferAlert;
